import { CustomButton, InformativeModal } from '@amili-v3/amili-v3-design';
import { Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export type DnaExtractionKit = {
  key: string;
  serial: string;
};

type DnaExtractionKitListModalProps = {
  open: boolean;
  onCancel: () => void;
  title: string;
  content: string;
  kits: DnaExtractionKit[];
  onConfirm: () => void;
  confirmLoading?: boolean;
  confirmDisabled?: boolean;
  // Selection is only meaningful when the caller can act on a subset of the
  // list (e.g. choosing which kits to include from a CSV upload). When the
  // list is already committed (e.g. completing an extraction batch), leave
  // this unset - the list is shown read-only.
  selectable?: boolean;
  selectedKeys?: string[];
  onToggleKitDnaExtractionBypass?: (key: string) => void;
  onToggleAllKitsDnaExtractionBypass?: () => void;
};

const DnaExtractionKitListModal = (props: DnaExtractionKitListModalProps) => {
  const {
    open,
    onCancel,
    title,
    content,
    kits,
    onConfirm,
    confirmLoading,
    confirmDisabled,
    selectable = false,
    selectedKeys = [],
    onToggleKitDnaExtractionBypass,
    onToggleAllKitsDnaExtractionBypass,
  } = props;
  const { t } = useTranslation();

  const isCheckAll = selectable && selectedKeys.length === kits.length;
  const isIndeterminate = selectable && selectedKeys.length > 0 && !isCheckAll;

  return (
    <InformativeModal
      error
      title=""
      icon={null}
      centered
      layout="vertical"
      open={open}
      onCancel={onCancel}
      footer={
        <CustomButton
          size="large"
          width="100%"
          key="confirm"
          onClick={onConfirm}
          disabled={confirmDisabled}
          loading={confirmLoading}
        >
          {t('common.confirm')}
        </CustomButton>
      }
    >
      <div>{title}</div>
      <KitListWrapper>
        <div>{content}</div>
        {selectable && (
          <label className="kit-row check-all">
            <Checkbox
              checked={isCheckAll}
              indeterminate={isIndeterminate}
              onChange={onToggleAllKitsDnaExtractionBypass}
            />
            <span>{t('common.checkAll')}</span>
          </label>
        )}
        <div className="kit-list">
          {kits.map(kit => (
            <label key={kit.key} className="kit-row">
              {selectable && (
                <Checkbox
                  checked={selectedKeys.includes(kit.key)}
                  onChange={() => onToggleKitDnaExtractionBypass?.(kit.key)}
                />
              )}
              <span className="kit-serial">{kit.serial}</span>
            </label>
          ))}
        </div>
      </KitListWrapper>
    </InformativeModal>
  );
};

const KitListWrapper = styled.div`
  margin-top: 8px;
  text-align: left;

  .check-all {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    cursor: pointer;
  }

  .kit-list {
    margin-top: 8px;
    max-height: 220px;
    overflow-y: auto;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 16px;
  }

  .kit-row {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: default;
    text-align: left;
  }

  .kit-serial {
    font-weight: 600;
    width: 100%;
    text-align: left;
  }
`;

export default DnaExtractionKitListModal;
